'use client';

import { useState, useTransition, KeyboardEvent } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import type { ZipValidationStatus, ZipSearchResult, ZipSearchLocationResult } from './types';
import { lookupZipInServiceArea, checkLocationPageExists } from '@/lib/actions';
import { ServiceLocationsSearchFormProps } from './types';

export function triggerScheduleModal() {
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  window.location.hash = 'schedule';
}

function normalizeLocationHref(link: string): string {
  return link
    .trim()
    .replace(/^\/+/, '')
    .replace(/^locations\//, '');
}

function getRenderableResults(searchResult: ZipSearchResult | null): ZipSearchLocationResult[] {
  if (!searchResult) return [];

  if (Array.isArray(searchResult.results) && searchResult.results.length > 0) {
    return searchResult.results;
  }

  if (searchResult.locality && searchResult.link) {
    return [
      {
        locality: searchResult.locality,
        link: searchResult.link,
        stateShort: searchResult.stateShort,
      },
    ];
  }

  return [];
}

export default function ServiceLocationsSearchForm({
  onSearch,
  stateFilter,
  initialStatus = 'idle',
  initialSearchResult = null,
  initialZip = '',
}: ServiceLocationsSearchFormProps) {
  const [zip, setZip] = useState(initialZip);
  const [status, setStatus] = useState<ZipValidationStatus>(initialStatus);
  const [, startTransition] = useTransition();
  const [searchResult, setSearchResult] = useState<ZipSearchResult | null>(initialSearchResult);
  const [outOfState, setOutOfState] = useState(false);

  const handleSearch = () => {
    const trimmed = zip.trim();

    if (trimmed.length !== 5 || !/^\d{5}$/.test(trimmed)) return;

    startTransition(async () => {
      const result = await lookupZipInServiceArea(trimmed);

      if (!result) {
        setStatus('invalid');
        setSearchResult(null);
        setOutOfState(false);
        onSearch?.({ lat: 0, lng: 0 }, 'invalid');
        return;
      }

      if (result.inServiceArea && result.lat !== null && result.lng !== null) {
        const stateSlug = result.stateShort?.toLowerCase();
        const filterSlug = stateFilter?.toLowerCase();

        if (filterSlug && stateSlug !== filterSlug) {
          setStatus('invalid');
          setSearchResult(null);
          setOutOfState(true);
          onSearch?.({ lat: 0, lng: 0 }, 'invalid');
          return;
        }

        setStatus('valid');
        setSearchResult(result);
        setOutOfState(false);
        onSearch?.({ lat: result.lat, lng: result.lng }, 'valid', result);
      } else {
        setStatus('invalid');
        setSearchResult(null);
        setOutOfState(false);
        onSearch?.({ lat: 0, lng: 0 }, 'invalid');
      }
    });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  const handleZipChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 5);
    setZip(digits);
    if (status !== 'idle') {
      setStatus('idle');
      setSearchResult(null);
      setOutOfState(false);
      onSearch?.({ lat: 0, lng: 0 }, 'idle');
    }
  };

  const isSearched = status !== 'idle';
  const renderableResults = getRenderableResults(searchResult);

  return (
    <div className='flex flex-col gap-3 w-full'>
      <div className='relative w-full'>
        {isSearched && (
          <div className='absolute z-10 flex items-center px-[4px] bg-white top-0 left-[11px] py-[2px] mt-[-2px]'>
            <span className='text-primary-500 font-instrument text-[10px] tracking-[-0.01em] leading-[130%]'>
              Enter your ZIP code
            </span>
          </div>
        )}

        <div
          className={`flex items-center w-full rounded-[8px] border-2 bg-white transition-colors duration-200 ${
            status === 'valid'
              ? 'border-[#16a085]'
              : status === 'invalid'
                ? 'border-[#e05252]'
                : 'border-[#e9e8e7]'
          } ${isSearched ? 'p-2 pl-0 gap-0 mt-2' : 'p-2 gap-4 mt-0'}`}
        >
          {/* Status icon — only shown after a search */}
          {isSearched && (
            <div className='flex shrink-0 items-center justify-center pl-4 pr-1'>
              {status === 'valid' && (
                <div className='flex items-center justify-center rounded-full w-[22px] h-[22px] bg-[#16a085]'>
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='white'
                  >
                    <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
                  </svg>
                </div>
              )}
              {status === 'invalid' && (
                <div className='flex items-center justify-center rounded-full w-[22px] h-[22px] bg-[#e05252]'>
                  <svg
                    width='12'
                    height='12'
                    viewBox='0 0 24 24'
                    fill='white'
                  >
                    <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
                  </svg>
                </div>
              )}
            </div>
          )}

          {/* ZIP input */}
          <input
            id='zip-code-input'
            type='text'
            inputMode='numeric'
            pattern='\d{5}'
            maxLength={5}
            value={zip}
            onChange={(event) => handleZipChange(event.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete='off'
            autoCorrect='off'
            autoCapitalize='off'
            spellCheck={false}
            placeholder={isSearched ? undefined : 'Enter your zip code'}
            aria-label='Enter your ZIP code'
            className={`flex-1 bg-transparent focus-within:border-secondary-500 focus-within:ring-2 focus-within:ring-secondary-500/30 h-12 pl-2 text-primary-500 placeholder:text-primary-500/40 font-instrument tracking-[-0.01em] leading-[150%] min-w-0 [&:-webkit-autofill]:shadow-[0_0_0_1000px_white_inset] [&:-webkit-autofill]:[-webkit-text-fill-color:rgb(var(--primary-500))] ${
              isSearched ? 'text-[14px]' : 'text-[16px]'
            }`}
          />

          {/* Search button */}
          <button
            id='zip-search-button'
            type='button'
            onClick={handleSearch}
            aria-label='Search by ZIP code'
            className={`flex shrink-0 items-center justify-center bg-brand-accent text-primary-btn-text hover:opacity-90 transition-colors cursor-pointer rounded-[4px] ${
              isSearched ? 'h-[40px] w-[40px] mr-[5px]' : 'h-[48px] w-[48px] mr-0'
            }`}
          >
            <div className='relative w-[16px] h-[16px]'>
              <svg
                width='14'
                height='14'
                viewBox='0 0 14 14'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <circle
                  cx='5.13142'
                  cy='5.13142'
                  r='4.13142'
                  stroke='currentColor'
                  strokeWidth='2'
                />
                <path
                  d='M9.07031 8.46875L12.6989 12.0974'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
            </div>
          </button>
        </div>
      </div>

      {/* ── Status message ── */}
      {isSearched && (
        <p className='text-primary-500 text-[13px] tracking-[-0.01em] leading-[140%]'>
          {status === 'valid'
            ? renderableResults.length > 1
              ? 'Your home is in our service area. Select your nearest location below.'
              : 'Your home is in our service area!'
            : outOfState
              ? `This ZIP code is not in our ${stateFilter?.toUpperCase()} service area.`
              : 'Your home is not in our service area.'}
        </p>
      )}

      {status === 'valid' && renderableResults.length > 0 && (
        <div className='flex flex-col gap-2'>
          {renderableResults.map((result) => (
            <LocalityItem
              key={`${result.link}-${result.locality}`}
              locality={result.locality}
              link={result.link}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function LocalityItem({ locality, link }: { locality: string; link: string }) {
  const router = useRouter();
  const [isChecking, setIsChecking] = useState(false);

  const normalizedLink = normalizeLocationHref(link);
  const href = `/locations/${normalizedLink}`;

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isChecking) return;
    setIsChecking(true);
    try {
      const exists = await checkLocationPageExists(normalizedLink);
      if (exists) {
        router.push(href);
      } else {
        triggerScheduleModal();
      }
    } catch (err) {
      console.error('[LocalityItem] validation failed:', err);
      triggerScheduleModal();
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <Link
      href={href}
      onClick={handleClick}
      className={`group flex items-center gap-3 rounded-lg px-4 py-3 transition-all duration-150 border-[1.5px] border-[#e8f5f2] bg-[#f6fdfb] hover:bg-[#e8f5f2] hover:border-[#16a085] ${
        isChecking ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      <span className='flex shrink-0 items-center justify-center rounded-sm w-[28px] h-[28px] bg-[#16a085]'>
        <svg
          width='13'
          height='13'
          viewBox='0 0 24 24'
          fill='white'
        >
          <path d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z' />
        </svg>
      </span>

      <span className='flex-1 font-instrument font-medium text-primary-500 text-[14px] tracking-[-0.01em]'>
        {locality}
      </span>

      <svg
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill='none'
        className='shrink-0 stroke-secondary-500 transition-transform duration-150 group-hover:translate-x-0.5'
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M5 12h14M13 6l6 6-6 6' />
      </svg>
    </Link>
  );
}
